import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import {
  deleteRoute,
  deleteRouteReview,
  getApiErrorMessage,
  getRoute,
  listAllRouteMedia,
  listAllRouteReviews,
  listRouteCategories,
  listRouteTags,
  listRoutes,
  publishRoute,
  type RouteItem,
  type RouteStatus,
} from '../lib/api';
import { extractTextFromContentBlocks, renderContentBlocksHtml } from '../lib/contentBlocks';
import { ROUTES_TEMPLATE } from '../templates/routesTemplate';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const statusLabel = (status: RouteStatus): string => {
  if (status === 'pending_review') return 'Pending Review';
  if (status === 'approved') return 'Approved';
  if (status === 'published') return 'Published';
  if (status === 'rejected') return 'Rejected';
  if (status === 'archived') return 'Archived';
  return 'Draft';
};

const statusBadgeClass = (status: RouteStatus): string => {
  if (status === 'published') return 'bg-emerald-50 text-emerald-700';
  if (status === 'pending_review') return 'bg-amber-50 text-amber-700';
  if (status === 'approved') return 'bg-blue-50 text-blue-700';
  if (status === 'rejected') return 'bg-rose-50 text-rose-700';
  if (status === 'archived') return 'bg-slate-200 text-slate-700';
  return 'bg-slate-100 text-slate-700';
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const normalizeUrl = (rawUrl: string): string => {
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith('/')) return `${window.location.origin}${rawUrl}`;
  return rawUrl;
};

const renderStarRow = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 1) {
    return '<span class="text-[11px] text-slate-400">No rating</span>';
  }
  return Array.from({ length: 5 }, (_, index) =>
    index < value
      ? '<span class="text-amber-500">★</span>'
      : '<span class="text-slate-300">★</span>'
  ).join('');
};

export default function RoutesPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => ROUTES_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const searchInput = root.querySelector<HTMLInputElement>('#routes-search-input');
    const statusFilter = root.querySelector<HTMLSelectElement>('#routes-status-filter');
    const featuredFilter = root.querySelector<HTMLSelectElement>('#routes-featured-filter');
    const categoryFilter = root.querySelector<HTMLSelectElement>('#routes-category-filter');
    const distanceMin = root.querySelector<HTMLInputElement>('#routes-distance-min-filter');
    const distanceMax = root.querySelector<HTMLInputElement>('#routes-distance-max-filter');
    const durationMin = root.querySelector<HTMLInputElement>('#routes-duration-min-filter');
    const durationMax = root.querySelector<HTMLInputElement>('#routes-duration-max-filter');
    const elevationMin = root.querySelector<HTMLInputElement>('#routes-elevation-min-filter');
    const elevationMax = root.querySelector<HTMLInputElement>('#routes-elevation-max-filter');
    const orderingFilter = root.querySelector<HTMLSelectElement>('#routes-ordering-filter');
    const tableBody = root.querySelector<HTMLTableSectionElement>('#routes-table-body');
    const resultsMeta = root.querySelector<HTMLElement>('#routes-results-meta');
    const resultsMetaInline = root.querySelector<HTMLElement>('#routes-results-meta-inline');
    const pageIndicator = root.querySelector<HTMLElement>('#routes-page-indicator');
    const prevBtn = root.querySelector<HTMLButtonElement>('#routes-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#routes-next-btn');
    const createBtn = root.querySelector<HTMLButtonElement>('#routes-create-btn');
    const kpiPublished = root.querySelector<HTMLElement>('#routes-kpi-published');
    const kpiPending = root.querySelector<HTMLElement>('#routes-kpi-pending');
    const kpiDraft = root.querySelector<HTMLElement>('#routes-kpi-draft');
    const kpiFeatured = root.querySelector<HTMLElement>('#routes-kpi-featured');

    const viewOverlay = root.querySelector<HTMLElement>('#routes-view-overlay');
    const viewBackdrop = root.querySelector<HTMLElement>('#routes-view-backdrop');
    const viewCloseBtn = root.querySelector<HTMLButtonElement>('#routes-view-close-btn');
    const viewError = root.querySelector<HTMLElement>('#routes-view-error');
    const viewTitle = root.querySelector<HTMLElement>('#routes-view-title');
    const viewSubtitle = root.querySelector<HTMLElement>('#routes-view-subtitle');
    const viewHero = root.querySelector<HTMLImageElement>('#routes-view-hero');
    const viewGallery = root.querySelector<HTMLElement>('#routes-view-gallery');
    const viewStatus = root.querySelector<HTMLElement>('#routes-view-status');
    const viewSource = root.querySelector<HTMLElement>('#routes-view-source');
    const viewFeatured = root.querySelector<HTMLElement>('#routes-view-featured');
    const viewContentFormat = root.querySelector<HTMLElement>('#routes-view-content-format');
    const viewIsFavorited = root.querySelector<HTMLElement>('#routes-view-is-favorited');
    const viewSlug = root.querySelector<HTMLElement>('#routes-view-slug');
    const viewLocationName = root.querySelector<HTMLElement>('#routes-view-location-name');
    const viewDistance = root.querySelector<HTMLElement>('#routes-view-distance');
    const viewDuration = root.querySelector<HTMLElement>('#routes-view-duration');
    const viewElevationGain = root.querySelector<HTMLElement>('#routes-view-elevation-gain');
    const viewElevationLoss = root.querySelector<HTMLElement>('#routes-view-elevation-loss');
    const viewFavorites = root.querySelector<HTMLElement>('#routes-view-favorites');
    const viewWaypointsCount = root.querySelector<HTMLElement>('#routes-view-waypoints-count');
    const viewStartCoords = root.querySelector<HTMLElement>('#routes-view-start-coords');
    const viewEndCoords = root.querySelector<HTMLElement>('#routes-view-end-coords');
    const viewDescription = root.querySelector<HTMLElement>('#routes-view-description');
    const viewContentPreview = root.querySelector<HTMLElement>('#routes-view-content-preview');
    const viewContentJson = root.querySelector<HTMLElement>('#routes-view-content-json');
    const viewWaypoints = root.querySelector<HTMLElement>('#routes-view-waypoints');
    const viewReviewComment = root.querySelector<HTMLElement>('#routes-view-review-comment');
    const viewReviewsCount = root.querySelector<HTMLElement>('#routes-view-reviews-count');
    const viewReviews = root.querySelector<HTMLElement>('#routes-view-reviews');
    const viewCreatedAt = root.querySelector<HTMLElement>('#routes-view-created-at');
    const viewPublishedAt = root.querySelector<HTMLElement>('#routes-view-published-at');
    const viewReviewedAt = root.querySelector<HTMLElement>('#routes-view-reviewed-at');
    const viewUpdatedAt = root.querySelector<HTMLElement>('#routes-view-updated-at');
    const viewCategories = root.querySelector<HTMLElement>('#routes-view-categories');
    const viewTags = root.querySelector<HTMLElement>('#routes-view-tags');
    const viewEditBtn = root.querySelector<HTMLButtonElement>('#routes-view-edit-btn');
    const viewPublishBtn = root.querySelector<HTMLButtonElement>('#routes-view-publish-btn');
    const viewDeleteBtn = root.querySelector<HTMLButtonElement>('#routes-view-delete-btn');

    if (
      !searchInput ||
      !statusFilter ||
      !featuredFilter ||
      !categoryFilter ||
      !distanceMin ||
      !distanceMax ||
      !durationMin ||
      !durationMax ||
      !elevationMin ||
      !elevationMax ||
      !orderingFilter ||
      !tableBody ||
      !resultsMeta ||
      !resultsMetaInline ||
      !pageIndicator ||
      !prevBtn ||
      !nextBtn ||
      !createBtn ||
      !kpiPublished ||
      !kpiPending ||
      !kpiDraft ||
      !kpiFeatured ||
      !viewOverlay ||
      !viewBackdrop ||
      !viewCloseBtn ||
      !viewError ||
      !viewTitle ||
      !viewSubtitle ||
      !viewHero ||
      !viewGallery ||
      !viewStatus ||
      !viewSource ||
      !viewFeatured ||
      !viewContentFormat ||
      !viewIsFavorited ||
      !viewSlug ||
      !viewLocationName ||
      !viewDistance ||
      !viewDuration ||
      !viewElevationGain ||
      !viewElevationLoss ||
      !viewFavorites ||
      !viewWaypointsCount ||
      !viewStartCoords ||
      !viewEndCoords ||
      !viewDescription ||
      !viewContentPreview ||
      !viewContentJson ||
      !viewWaypoints ||
      !viewReviewComment ||
      !viewReviewsCount ||
      !viewReviews ||
      !viewCreatedAt ||
      !viewPublishedAt ||
      !viewReviewedAt ||
      !viewUpdatedAt ||
      !viewCategories ||
      !viewTags ||
      !viewEditBtn ||
      !viewPublishBtn ||
      !viewDeleteBtn
    ) {
      return;
    }

    let disposed = false;
    let searchTimer: number | null = null;
    let currentPage = 1;
    const pageSize = 20;
    let openedRouteId: number | null = null;
    let viewRequestId = 0;
    let routeCategoryMap = new Map<number, string>();
    let routeTagMap = new Map<number, string>();

    const showViewError = (message: string) => {
      viewError.classList.remove('hidden');
      viewError.textContent = message;
    };

    const hideViewError = () => {
      viewError.classList.add('hidden');
      viewError.textContent = '';
    };

    const updateViewParam = (value: string | null) => {
      const params = new URLSearchParams(window.location.search);
      if (value) params.set('view', value);
      else params.delete('view');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    };

    const closeView = () => {
      openedRouteId = null;
      viewRequestId += 1;
      viewOverlay.classList.add('hidden');
      updateViewParam(null);
    };

    const renderRows = (items: RouteItem[]) => {
      if (!items.length) {
        tableBody.innerHTML = '<tr><td class="px-4 py-8 text-slate-500 text-sm" colspan="14">No routes found.</td></tr>';
        return;
      }

      tableBody.innerHTML = items
        .map((item) => {
          const isOpened = openedRouteId === item.id;
          return `
            <tr class="hover:bg-slate-50 transition-colors ${isOpened ? 'bg-primary/5' : ''}">
              <td class="px-4 py-4 text-xs font-mono text-slate-400">#RT-${item.id}</td>
              <td class="px-4 py-4">
                <button data-route-action="view" data-route-id="${item.id}" class="font-bold text-slate-900 hover:text-primary text-left" type="button">${escapeHtml(item.title)}</button>
                <p class="text-xs text-slate-500">${escapeHtml(item.slug)}</p>
              </td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(item.location_name || '-')}</td>
              <td class="px-4 py-4"><span class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${statusBadgeClass(item.status)}">${statusLabel(item.status)}</span></td>
              <td class="px-4 py-4 text-center">${item.is_featured ? '<span class="material-symbols-outlined text-amber-500 text-[18px]">star</span>' : '-'}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(`${item.distance_km} km`)}</td>
              <td class="px-4 py-4 text-slate-600">${item.duration_minutes} min</td>
              <td class="px-4 py-4 text-slate-600">${item.elevation_gain_m}/${item.elevation_loss_m} m</td>
              <td class="px-4 py-4 text-center text-slate-700 font-semibold">${item.waypoints_count}</td>
              <td class="px-4 py-4 text-center text-slate-700 font-semibold">${item.favorites_count}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(item.source)}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(formatDateTime(item.created_at))}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(formatDateTime(item.updated_at))}</td>
              <td class="px-4 py-4 sticky right-0 bg-white">
                <div class="flex items-center justify-end gap-1 text-slate-400">
                  <button data-route-action="view" data-route-id="${item.id}" class="p-1 hover:text-primary" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">visibility</span></button>
                  <button data-route-action="edit" data-route-id="${item.id}" class="p-1 hover:text-primary" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">edit</span></button>
                  <button data-route-action="delete" data-route-id="${item.id}" class="p-1 hover:text-rose-500" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">delete</span></button>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');
    };

    const loadDictionaries = async () => {
      try {
        const [categories, tags] = await Promise.all([listRouteCategories(), listRouteTags()]);
        if (disposed) return;
        routeCategoryMap = new Map(categories.map((category) => [category.id, category.name]));
        routeTagMap = new Map(tags.map((tag) => [tag.id, tag.name]));
        categoryFilter.innerHTML = '<option value="">All Categories</option>';
        for (const category of categories) {
          categoryFilter.insertAdjacentHTML('beforeend', `<option value="${category.id}">${escapeHtml(category.name)}</option>`);
        }
      } catch {
        // no-op
      }
    };

    const loadCounters = async () => {
      try {
        const [published, pending, draft, featured] = await Promise.all([
          listRoutes({ status: 'published', page: 1, page_size: 1 }),
          listRoutes({ status: 'pending_review', page: 1, page_size: 1 }),
          listRoutes({ status: 'draft', page: 1, page_size: 1 }),
          listRoutes({ featured: true, page: 1, page_size: 1 }),
        ]);
        if (disposed) return;
        kpiPublished.textContent = String(published.count);
        kpiPending.textContent = String(pending.count);
        kpiDraft.textContent = String(draft.count);
        kpiFeatured.textContent = String(featured.count);
      } catch {
        if (disposed) return;
        kpiPublished.textContent = '-';
        kpiPending.textContent = '-';
        kpiDraft.textContent = '-';
        kpiFeatured.textContent = '-';
      }
    };

    const loadRoutes = async () => {
      try {
        const params: Record<string, string | number | boolean | undefined> = {
          page: currentPage,
          page_size: pageSize,
          search: searchInput.value.trim() || undefined,
          status: statusFilter.value !== 'all' ? statusFilter.value : undefined,
          featured: featuredFilter.value === 'all' ? undefined : featuredFilter.value,
          category_ids: categoryFilter.value || undefined,
          distance_min: distanceMin.value || undefined,
          distance_max: distanceMax.value || undefined,
          duration_min: durationMin.value || undefined,
          duration_max: durationMax.value || undefined,
          elevation_min: elevationMin.value || undefined,
          elevation_max: elevationMax.value || undefined,
          ordering: orderingFilter.value || undefined,
        };

        const payload = await listRoutes(params);
        if (disposed) return;

        renderRows(payload.results);
        const totalPages = Math.max(1, Math.ceil(payload.count / pageSize));
        pageIndicator.textContent = `Page ${currentPage} / ${totalPages}`;
        prevBtn.disabled = currentPage <= 1;
        nextBtn.disabled = !payload.next;
        const from = payload.count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const to = payload.count === 0 ? 0 : Math.min(currentPage * pageSize, payload.count);
        resultsMeta.textContent = `Showing ${from}-${to} of ${payload.count} routes`;
        resultsMetaInline.textContent = `Showing ${from}-${to} of ${payload.count} routes`;
      } catch (error) {
        if (disposed) return;
        tableBody.innerHTML = `<tr><td class="px-4 py-8 text-rose-600 text-sm" colspan="14">${escapeHtml(getApiErrorMessage(error, 'Failed to load routes'))}</td></tr>`;
      }
    };

    const openView = async (routeId: number) => {
      openedRouteId = routeId;
      const requestId = ++viewRequestId;
      hideViewError();
      viewOverlay.classList.remove('hidden');
      updateViewParam(String(routeId));
      viewTitle.textContent = 'Loading...';
      viewSubtitle.textContent = `ID: ${routeId}`;
      viewStatus.textContent = 'Loading';
      viewSource.textContent = '-';
      viewFeatured.textContent = '-';
      viewContentFormat.textContent = '-';
      viewIsFavorited.textContent = '-';
      viewSlug.textContent = '-';
      viewLocationName.textContent = '-';
      viewDistance.textContent = '-';
      viewDuration.textContent = '-';
      viewElevationGain.textContent = '-';
      viewElevationLoss.textContent = '-';
      viewFavorites.textContent = '-';
      viewWaypointsCount.textContent = '-';
      viewStartCoords.textContent = '-';
      viewEndCoords.textContent = '-';
      viewDescription.textContent = 'Loading...';
      viewContentPreview.innerHTML = '<span class="text-slate-500">Loading...</span>';
      viewContentJson.textContent = '[]';
      viewReviewComment.textContent = '-';
      viewReviewsCount.textContent = '0';
      viewReviews.innerHTML = '<p class="text-sm text-slate-500">Loading reviews...</p>';
      viewCreatedAt.textContent = '-';
      viewPublishedAt.textContent = '-';
      viewReviewedAt.textContent = '-';
      viewUpdatedAt.textContent = '-';
      viewCategories.innerHTML = '<span class="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">-</span>';
      viewTags.innerHTML = '<span class="px-2 py-1 border border-slate-200 text-slate-400 text-xs rounded-lg font-medium">-</span>';
      viewGallery.innerHTML = '';
      viewWaypoints.innerHTML = '';
      viewHero.src =
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>Loading</text></svg>";

      try {
        const [route, media, reviews] = await Promise.all([getRoute(routeId), listAllRouteMedia(routeId), listAllRouteReviews(routeId)]);
        if (disposed || requestId !== viewRequestId || openedRouteId !== routeId) return;

        viewTitle.textContent = route.title;
        viewSubtitle.textContent = `ID: #RT-${route.id}`;
        viewStatus.textContent = statusLabel(route.status);
        viewStatus.className = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${statusBadgeClass(route.status)}`;
        viewSource.textContent = route.source;
        viewFeatured.textContent = route.is_featured ? 'Featured' : 'Not Featured';
        viewContentFormat.textContent = route.content_format || 'blocks_v1';
        viewIsFavorited.textContent = route.is_favorited ? 'Favorited' : 'Not Favorited';
        viewFeatured.className = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
          route.is_featured ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
        }`;
        viewContentFormat.className = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-700';
        viewIsFavorited.className = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
          route.is_favorited ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'
        }`;
        viewSlug.textContent = route.slug;
        viewLocationName.textContent = route.location_name || '-';
        viewDistance.textContent = `${route.distance_km} km`;
        viewDuration.textContent = `${route.duration_minutes} min`;
        viewElevationGain.textContent = `${route.elevation_gain_m} m`;
        viewElevationLoss.textContent = `${route.elevation_loss_m} m`;
        viewFavorites.textContent = String(route.favorites_count);
        viewWaypointsCount.textContent = String(route.waypoints_count);
        viewStartCoords.textContent = route.start_latitude && route.start_longitude ? `${route.start_latitude}, ${route.start_longitude}` : '-';
        viewEndCoords.textContent = route.end_latitude && route.end_longitude ? `${route.end_latitude}, ${route.end_longitude}` : '-';

        const fallbackText = route.description || extractTextFromContentBlocks(route.content_blocks) || '-';
        viewDescription.textContent = fallbackText;
        const renderedBlocks = renderContentBlocksHtml(route.content_blocks);
        viewContentPreview.innerHTML = renderedBlocks || '<span class=\"text-slate-500\">No structured blocks.</span>';
        viewContentJson.textContent = JSON.stringify(route.content_blocks || [], null, 2);
        viewReviewComment.textContent = route.review_comment || '-';
        const reviewsSummary = reviews.summary ?? route.reviews_summary;
        viewReviewsCount.textContent = reviewsSummary
          ? `${reviewsSummary.average_rating.toFixed(1)} avg • ${reviewsSummary.reviews_count} reviews`
          : String(reviews.count);
        viewReviews.innerHTML = reviews.results.length
          ? reviews.results
              .slice(0, 20)
              .map(
                (review) => `
                  <article class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div class="mb-2 flex items-start justify-between gap-3">
                      <div class="flex min-w-0 items-center gap-2">
                        ${
                          review.author.avatar
                            ? `<img class="h-8 w-8 rounded-full object-cover border border-slate-200" src="${escapeHtml(
                                review.author.avatar
                              )}" alt="${escapeHtml(review.author.name)}"/>`
                            : `<div class="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-bold text-slate-600">${escapeHtml(
                                (review.author.name || '?').slice(0, 1).toUpperCase()
                              )}</div>`
                        }
                        <div class="min-w-0">
                          <p class="truncate text-xs font-semibold text-slate-700">${escapeHtml(review.author.name || 'Unknown user')}</p>
                          <div class="text-[11px] leading-none">${renderStarRow(review.rating_value)}</div>
                        </div>
                      </div>
                      <span class="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-700">${escapeHtml(review.status)}</span>
                    </div>
                    <p class="text-sm text-slate-700">${escapeHtml((review.message || '').trim() || 'No text provided.')}</p>
                    <div class="mt-1 flex items-center justify-between gap-3">
                      <p class="text-[11px] text-slate-500">${escapeHtml(formatDateTime(review.created_at))}</p>
                      <button
                        type="button"
                        class="inline-flex items-center rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700"
                        data-route-review-delete-id="${review.id}"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                `
              )
              .join('')
          : '<p class="text-sm text-slate-500">No reviews yet.</p>';
        viewCreatedAt.textContent = formatDateTime(route.created_at);
        viewPublishedAt.textContent = formatDateTime(route.published_at);
        viewReviewedAt.textContent = formatDateTime(route.reviewed_at);
        viewUpdatedAt.textContent = formatDateTime(route.updated_at);
        viewCategories.innerHTML =
          route.category_ids
            .map((id) => routeCategoryMap.get(id))
            .filter(Boolean)
            .map((name) => `<span class="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">${escapeHtml(name as string)}</span>`)
            .join('') || '<span class="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">No categories</span>';
        viewTags.innerHTML =
          route.tag_ids
            .map((id) => routeTagMap.get(id))
            .filter(Boolean)
            .map((name) => `<span class="px-2 py-1 border border-slate-200 text-slate-500 text-xs rounded-lg font-medium">#${escapeHtml(name as string)}</span>`)
            .join('') || '<span class="px-2 py-1 border border-slate-200 text-slate-400 text-xs rounded-lg font-medium">No tags</span>';

        viewWaypoints.innerHTML =
          route.waypoints
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order)
            .map(
              (waypoint) => `
                <div class="rounded-lg border border-slate-200 px-3 py-2">
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-semibold text-slate-900">#${waypoint.sort_order} ${escapeHtml(waypoint.title || waypoint.location_name || waypoint.stop_type || 'Waypoint')}</p>
                    <span class="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 uppercase">${escapeHtml(waypoint.stop_type || 'via')}</span>
                  </div>
                  <p class="text-xs text-primary mt-1">${escapeHtml(
                    waypoint.latitude && waypoint.longitude ? `${waypoint.latitude}, ${waypoint.longitude}` : '-'
                  )}</p>
                  ${waypoint.note ? `<p class="text-xs text-slate-500 mt-1">${escapeHtml(waypoint.note)}</p>` : ''}
                </div>
              `
            )
            .join('') || '<p class="text-sm text-slate-500">No waypoints.</p>';

        const mediaItems = media.results.map((item) => item.file_large_url || item.file_url).filter(Boolean) as string[];
        const coverCandidate = route.cover_image || mediaItems[0] || '';
        if (coverCandidate) {
          viewHero.src = normalizeUrl(coverCandidate);
        } else {
          viewHero.src =
            "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>No media</text></svg>";
        }
        viewGallery.innerHTML = mediaItems
          .slice(0, 8)
          .map(
            (url) => `
              <div class="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                <img class="w-full h-full object-cover" alt="Route media" src="${escapeHtml(normalizeUrl(url))}"/>
              </div>
            `
          )
          .join('');

        viewEditBtn.onclick = () => navigate(`/routes/create?id=${route.id}`);
        viewPublishBtn.onclick = async () => {
          try {
            await publishRoute(route.id);
            await openView(route.id);
            await Promise.all([loadRoutes(), loadCounters()]);
          } catch (error) {
            showViewError(getApiErrorMessage(error, 'Failed to publish route.'));
          }
        };
        viewDeleteBtn.onclick = async () => {
          if (!window.confirm(`Delete route "${route.title}"?`)) return;
          try {
            await deleteRoute(route.id);
            closeView();
            await Promise.all([loadRoutes(), loadCounters()]);
          } catch (error) {
            showViewError(getApiErrorMessage(error, 'Failed to delete route.'));
          }
        };
        viewReviews.onclick = async (event) => {
          const target = event.target as HTMLElement;
          const button = target.closest<HTMLButtonElement>('[data-route-review-delete-id]');
          if (!button) return;
          const reviewId = Number(button.dataset.routeReviewDeleteId);
          if (Number.isNaN(reviewId)) return;
          if (!window.confirm(`Delete review #${reviewId}?`)) return;
          try {
            await deleteRouteReview(route.id, reviewId);
            await openView(route.id);
            await Promise.all([loadRoutes(), loadCounters()]);
          } catch (error) {
            showViewError(getApiErrorMessage(error, 'Failed to delete route review.'));
          }
        };
      } catch (error) {
        if (disposed || requestId !== viewRequestId || openedRouteId !== routeId) return;
        showViewError(getApiErrorMessage(error, 'Failed to load route details.'));
      }
    };

    const onTableClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const actionEl = target.closest<HTMLElement>('[data-route-action][data-route-id]');
      if (!actionEl) return;
      const action = actionEl.dataset.routeAction;
      const routeId = Number(actionEl.dataset.routeId);
      if (!Number.isFinite(routeId) || routeId <= 0) return;

      if (action === 'view') {
        void openView(routeId);
        return;
      }
      if (action === 'edit') {
        navigate(`/routes/create?id=${routeId}`);
        return;
      }
      if (action === 'delete') {
        if (!window.confirm('Delete this route?')) return;
        void (async () => {
          try {
            await deleteRoute(routeId);
            await Promise.all([loadRoutes(), loadCounters()]);
          } catch (error) {
            window.alert(getApiErrorMessage(error, 'Failed to delete route.'));
          }
        })();
      }
    };

    const triggerReload = () => {
      currentPage = 1;
      void loadRoutes();
    };

    void loadDictionaries();
    void Promise.all([loadRoutes(), loadCounters()]);

    const params = new URLSearchParams(window.location.search);
    const viewId = Number(params.get('view') || '');
    if (Number.isFinite(viewId) && viewId > 0) {
      void openView(viewId);
    }

    const onSearchInput = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => triggerReload(), 250);
    };

    const onFilterChange = () => triggerReload();
    const resetBtn = root.querySelector<HTMLButtonElement>('#routes-reset-filters-btn');
    const onPrev = () => {
      if (currentPage <= 1) return;
      currentPage -= 1;
      void loadRoutes();
    };
    const onNext = () => {
      currentPage += 1;
      void loadRoutes();
    };

    searchInput.addEventListener('input', onSearchInput);
    statusFilter.addEventListener('change', onFilterChange);
    featuredFilter.addEventListener('change', onFilterChange);
    categoryFilter.addEventListener('change', onFilterChange);
    distanceMin.addEventListener('input', onFilterChange);
    distanceMax.addEventListener('input', onFilterChange);
    durationMin.addEventListener('input', onFilterChange);
    durationMax.addEventListener('input', onFilterChange);
    elevationMin.addEventListener('input', onFilterChange);
    elevationMax.addEventListener('input', onFilterChange);
    orderingFilter.addEventListener('change', onFilterChange);
    resetBtn?.addEventListener('click', () => {
      searchInput.value = '';
      statusFilter.value = 'all';
      featuredFilter.value = 'all';
      categoryFilter.value = '';
      distanceMin.value = '';
      distanceMax.value = '';
      durationMin.value = '';
      durationMax.value = '';
      elevationMin.value = '';
      elevationMax.value = '';
      orderingFilter.value = '-created_at';
      triggerReload();
    });
    prevBtn.addEventListener('click', onPrev);
    nextBtn.addEventListener('click', onNext);
    tableBody.addEventListener('click', onTableClick);
    createBtn.addEventListener('click', () => navigate('/routes/create'));
    viewCloseBtn.addEventListener('click', closeView);
    viewBackdrop.addEventListener('click', closeView);

    return () => {
      disposed = true;
      if (searchTimer) window.clearTimeout(searchTimer);
      searchInput.removeEventListener('input', onSearchInput);
      statusFilter.removeEventListener('change', onFilterChange);
      featuredFilter.removeEventListener('change', onFilterChange);
      categoryFilter.removeEventListener('change', onFilterChange);
      distanceMin.removeEventListener('input', onFilterChange);
      distanceMax.removeEventListener('input', onFilterChange);
      durationMin.removeEventListener('input', onFilterChange);
      durationMax.removeEventListener('input', onFilterChange);
      elevationMin.removeEventListener('input', onFilterChange);
      elevationMax.removeEventListener('input', onFilterChange);
      orderingFilter.removeEventListener('change', onFilterChange);
      prevBtn.removeEventListener('click', onPrev);
      nextBtn.removeEventListener('click', onNext);
      tableBody.removeEventListener('click', onTableClick);
      viewCloseBtn.removeEventListener('click', closeView);
      viewBackdrop.removeEventListener('click', closeView);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Routes | MotoJourney</title>
      </Helmet>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
    </>
  );
}
